<script setup lang="ts">
import { Button } from '@/components/ui/button'
import { AutoForm } from '@/ui-plus/auto-form'
import { toTypedSchema } from '@vee-validate/zod'
import { useForm } from 'vee-validate'
import { z } from "zod";
import { useFormPlus } from '@/ui-plus/form/useFormPlus'
import { Loader2 } from 'lucide-vue-next'
import { deliveryteamOrdersApiClient } from '@api-client/deliveryteam_orders';
import { onMounted } from 'vue';
import { toast } from '@/ui-plus/sonner';
import { emailArraySchema } from "@lib/zod/EmailArray";
import { phoneNumberArraySchema } from "@lib/zod/PhoneNumberArray";
import AutoFormFieldPhone from '@/ui-plus/PhoneNumber/AutoFormFieldPhone.vue';

const schema = z.object({
    orderNotificationEmails: emailArraySchema.optional(),
    supportBuyerContactPhoneNumbers: phoneNumberArraySchema.optional(),
    supportReplyToEmails: emailArraySchema.optional(),
})

const form = useForm({
    validationSchema: toTypedSchema(schema),
})

const { isLoading, globalError, handleError, withSubmit } = useFormPlus(form);

const props = defineProps<{
    deliveryteamId: string;
}>();

onMounted(async () => {
    isLoading.value = true;
    await loadData();
    isLoading.value = false;
});

const loadData = async () => {
    const { data, error } = await (await deliveryteamOrdersApiClient.easy_api.deliveryteam_contact_info[':id']['data.json'].$get({
        param: {
            id: props.deliveryteamId,
        },
    })).json()
    if (error) {
        handleError(error, form);
    } else {
        if (data) {
            form.setFieldValue('orderNotificationEmails', data['orderNotificationEmails'] ?? []);
            form.setFieldValue('supportBuyerContactPhoneNumbers', data['supportBuyerContactPhoneNumbers'] ?? []);
            form.setFieldValue('supportReplyToEmails', data['supportReplyToEmails'] ?? []);
        }
    }
}

async function onSubmit(values: z.infer<typeof schema>) {
    await withSubmit(async () => {
        const { error } = await (await deliveryteamOrdersApiClient.easy_api.deliveryteam_contact_info[':id'].$patch({
            param: {
                id: props.deliveryteamId,
            },
            json: values
        })).json()
        if (error) {
            toast.error("Failed to save");
            handleError(error, form);
        } else {
            toast.success(`Saved`);
        }
    })
}

</script>

<template>
    <AutoForm class="space-y-6"
              :schema="schema"
              :form="form"
              :field-config="{
                orderNotificationEmails: {
                    label: 'Order Notifications',
                    description: 'for incoming orders, print errors, late action reminders, etc.',
                    inputProps: {
                        // placeholder: 'orders@example.com'
                    }
                },
                supportBuyerContactPhoneNumbers: {
                    label: 'Order Support Phone Numbers',
                    description: 'Buyer will see these phone numbers in email notification once they place an order.',
                    inputProps: {
                        // @ts-ignore
                        newComponent: AutoFormFieldPhone,
                    }
                },
                supportReplyToEmails: {
                    label: 'Order Support Reply To Emails',
                    description: 'Buyer will reply to these email addresses when they have questions about their order.',
                    inputProps: {
                        // placeholder: ''
                    }
                },
            }"
              @submit="onSubmit">
        <div class="text-sm font-medium text-destructive"
             v-if="globalError">
            {{ globalError }}
        </div>
        <div>
            <Button type="submit"
                    :disabled="isLoading"
                    class="w-full my-6">
                <Loader2 v-if="isLoading"
                         class="animate-spin" />
                Save
            </Button>
        </div>
    </AutoForm>
</template>
